import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Textarea,
  Switch,
  Label,
} from '@artstorefronts/ui';
import { ListPlusIcon, WandSparklesIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CREATE_USER_COMPLETION_TYPE_RULE, UPDATE_USER_COMPLETION_TYPE_RULE } from '../graphql/mutations';
import { COMPLETION_TYPES_QUERY } from '../graphql/queries';
import { useAnalytics } from '../context/analytics';

interface CustomRulesDialogProps {
  completionType: any;
  isDisabled: boolean;
  compact?: boolean;
}

interface FormData {
  rules: string;
  enabled: boolean;
}

export default function CustomRulesDialog({ completionType, isDisabled, compact }: CustomRulesDialogProps) {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const { amplitude } = useAnalytics();
  const [open, setOpen] = useState(false);
  const [localEnabled, setLocalEnabled] = useState(completionType?.userRules?.active || false);

  const [createRule] = useMutation(CREATE_USER_COMPLETION_TYPE_RULE, {
    refetchQueries: [{ query: COMPLETION_TYPES_QUERY }],
  });
  const [updateRule] = useMutation(UPDATE_USER_COMPLETION_TYPE_RULE, {
    refetchQueries: [{ query: COMPLETION_TYPES_QUERY }],
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      rules: completionType?.userRules?.rules,
      enabled: completionType?.userRules?.active,
    },
  });

  const handleReset = () => {
    reset({
      rules: completionType?.userRules?.rules || '',
      enabled: completionType?.userRules?.active || false,
    });
    setLocalEnabled(completionType?.userRules?.active || false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleReset();
    }
    setOpen(isOpen);
  };

  useEffect(() => {
    reset({
      rules: completionType?.userRules?.rules || '',
      enabled: completionType?.userRules?.active || false,
    });
  }, [completionType]);

  const onSave = async (data: FormData, e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const rules = data.rules?.trim() || '';

    try {
      if (completionType?.userRules?.id) {
        // Update existing rule
        await updateRule({
          variables: {
            input: {
              id: completionType.userRules.id,
              rules,
              active: data.enabled,
            },
          },
        });
      } else {
        // Create new rule
        await createRule({
          variables: {
            input: {
              completionTypeId: completionType.id,
              rules,
              active: data.enabled,
            },
          },
        });
      }

      amplitude.track('custom_rules_saved', { custom_rules: data.enabled, rules });

      toast.success(t('custom-rules-updated'));
      setOpen(false);
    } catch (error) {
      console.error('Error saving custom rules:', error);
      toast.error(t('failed-to-save-custom-rules'));
    }
  };

  useEffect(() => {
    setLocalEnabled(completionType?.userRules?.active || false);
  }, [completionType?.userRules?.active]);

  const handleSwitchToggle = async (enabled: boolean) => {
    // Update local state immediately
    setLocalEnabled(enabled);

    if (!completionType?.userRules?.id) {
      setValue('enabled', enabled, { shouldDirty: true });
      setOpen(true);
      return;
    }

    try {
      await updateRule({
        variables: {
          input: {
            id: completionType.userRules.id,
            // The toggle only flips enable/disable. Never send rules here —
            // form state can be stale (pre-reset), and coercing an unset value
            // to '' would wipe the saved rule text. Rule content is owned by
            // onSave (which trims and normalizes blank to '').
            active: enabled,
          },
        },
      });
      toast.success(enabled ? t('custom-rules-enabled') : t('custom-rules-disabled'));
    } catch (error) {
      // Revert local state on error
      setLocalEnabled(!enabled);
      const message = enabled ? t('failed-to-enable-custom-rules') : t('failed-to-disable-custom-rules');
      console.error(message, error);
      toast.error(message);

      setValue('enabled', !enabled, { shouldDirty: false });
    }
  };

  const handleOpenClick = () => {
    setOpen(true);
  };

  return (
    <>
      <div className="flex items-center">
        <Switch checked={localEnabled} onCheckedChange={handleSwitchToggle} disabled={isDisabled} />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-2 font-bold py-1 px-2"
          onClick={handleOpenClick}
          disabled={isDisabled}
        >
          {compact ? <ListPlusIcon className="w-6 h-6" /> : t('custom-rules')}
        </Button>
      </div>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center text-xl">
              <WandSparklesIcon className="w-6 h-6 mr-2" /> {completionType?.name || t('writer')}
            </DialogTitle>
            <DialogDescription className="text-left">{t('custom-rules-description')}</DialogDescription>
          </DialogHeader>

          <div>
            <div className="mb-4">
              <Label htmlFor="rules">{t('custom-rules-for-spark')}</Label>
              <Textarea {...register('rules')} placeholder={t('custom-rules-placeholder')} className="mt-2 h-[200px]" />
            </div>

            <div className="flex items-center space-x-2 mb-6">
              <Switch
                checked={watch('enabled')}
                onCheckedChange={(e) => {
                  setValue('enabled', e, { shouldDirty: true });
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              <Label htmlFor="enabled">{tc('enabled')}</Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleReset();
                  setOpen(false);
                }}
              >
                {tc('cancel')}
              </Button>
              <Button
                type="button"
                disabled={!isDirty}
                onClick={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(onSave)(e);
                }}
              >
                {t('save-custom-rules')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
